import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useAuth } from '@/lib/auth-context'
import { useTenant } from '@/lib/tenant-context'
import {
  Button,
  Input,
  Label,
} from '@lead-magnifier/ui'
import { CheckCircle2, ArrowLeft } from 'lucide-react'

export const Route = createFileRoute('/register')({
  component: RegisterPage,
})

function RegisterPage() {
  const { signUp } = useAuth()
  const tenant = useTenant()
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password !== confirmPassword) {
      setError('Passwörter stimmen nicht überein')
      return
    }

    if (password.length < 6) {
      setError('Passwort muss mindestens 6 Zeichen lang sein')
      return
    }

    setLoading(true)

    try {
      await signUp(email, password, name)
      setSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registrierung fehlgeschlagen')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-warm bg-noise flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm text-center animate-fade-in-up">
          <div className="animate-bounce-in">
            <div className="mx-auto mb-6 h-16 w-16 rounded-full bg-green-50 flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
          </div>
          <h1 className="font-display text-2xl font-bold">E-Mail bestätigen</h1>
          <p className="mt-3 text-muted-foreground text-sm leading-relaxed">
            Wir haben eine E-Mail an <strong className="text-foreground">{email}</strong> gesendet.
            Klicke auf den Link in der E-Mail, um dein Konto zu aktivieren.
          </p>
          <Link to="/login" className="block mt-8">
            <Button variant="outline" className="w-full h-12 btn-premium">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Zurück zum Login
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-warm bg-noise flex">
      {/* Side panel — hidden on mobile */}
      <div
        className="hidden lg:flex w-1/3 flex-col justify-end p-12 text-white"
        style={{ backgroundColor: tenant.branding.primary_color }}
      >
        <h2 className="font-display text-3xl font-bold leading-tight">
          Starte jetzt.<br />
          Kostenlos.
        </h2>
        <p className="mt-4 text-white/60 text-sm leading-relaxed">
          In wenigen Minuten bereit für deine erste Messe.
        </p>
      </div>

      {/* Register form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm animate-fade-in-up">
          <div className="text-center mb-10">
            <img
              src={tenant.branding.logo_url}
              alt={tenant.branding.name}
              className="h-10 w-auto mx-auto mb-8 object-contain"
            />
            <h1 className="font-display text-3xl font-bold tracking-tight">
              Konto erstellen
            </h1>
            <p className="mt-2 text-muted-foreground text-sm">
              Registriere dich, um Leads zu sammeln
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="rounded-lg bg-destructive/8 border border-destructive/20 px-4 py-3 text-sm text-destructive animate-scale-in">
                {error}
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Name
              </Label>
              <Input
                id="name"
                type="text"
                placeholder="Max Mustermann"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="h-12 bg-white border-border/60 focus:border-primary transition-colors"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                E-Mail
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="name@beispiel.at"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 bg-white border-border/60 focus:border-primary transition-colors"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Passwort
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Min. 6 Zeichen"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 bg-white border-border/60 focus:border-primary transition-colors"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirmPassword" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Bestätigen
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="h-12 bg-white border-border/60 focus:border-primary transition-colors"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 text-sm font-semibold tracking-wide btn-premium rounded-lg mt-2"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Wird registriert...
                </span>
              ) : (
                'Konto erstellen'
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-8">
            Bereits registriert?{' '}
            <Link to="/login" className="text-foreground font-medium hover:text-primary transition-colors">
              Anmelden
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
